"use client";

import { useState, useEffect, useCallback } from "react";
import { SearchFilters } from "@/app/components/search-filters";
import { ResultsTable } from "@/app/components/results-table";
import { searchRecords, getFilterOptions } from "@/app/lib/api";
import type { SearchParams, SearchResponse, FilterOptions } from "@/app/lib/types";

export default function SearchPage() {
  const [filters, setFilters] = useState<SearchParams>({ page: 1, page_size: 20 });
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterError, setFilterError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    getFilterOptions()
      .then(setFilterOptions)
      .catch((e) => { setFilterError(String(e)); console.error(e); });
  }, []);

  const doSearch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await searchRecords({ ...filters });
      setResults(res);
    } catch (e) {
      setError("Failed to load records. Is the backend running?");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (filterOptions) {
      doSearch();
    }
  }, [doSearch, filterOptions]);

  return (
    <div className="flex h-full">
      <div className="fixed top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 via-emerald-400 to-violet-500 z-50" />
      {showFilters && (
        <aside className="w-80 border-r p-4 overflow-y-auto bg-gray-50/80 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent">Filters</h2>
            <button
              onClick={() => setShowFilters(false)}
              className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer"
              title="Hide filters"
            >
              ✕
            </button>
          </div>
          {filterOptions ? (
            <SearchFilters
              options={filterOptions}
              filters={filters}
              onChange={(newFilters) => setFilters(newFilters)}
            />
          ) : filterError ? (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700 text-sm">
              Failed to load filters: {filterError}
            </div>
          ) : (
            <div className="text-sm text-gray-500">Loading filters...</div>
          )}
        </aside>
      )}

      <main className="flex-1 p-6 overflow-y-auto">
        <div className="flex items-center gap-6 mb-6 p-4 -mx-6 -mt-6 border-b bg-gradient-to-r from-blue-50/80 via-emerald-50/50 to-transparent">
          {!showFilters && (
            <button
              onClick={() => setShowFilters(true)}
              className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer"
              title="Show filters"
            >
              ☰ Filters
            </button>
          )}
          <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-emerald-800 bg-clip-text text-transparent">ECG Library</h1>
          <a href="/" className="ml-auto text-xs text-gray-400 hover:text-gray-600 underline">Back to main</a>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="animate-pulse text-gray-500">Loading...</div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-4 text-red-700 text-sm">
            {error}
          </div>
        )}

        {results && !loading && (
          <ResultsTable
            results={results}
            onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
          />
        )}
      </main>
    </div>
  );
}
